import { useCreateDataTableOptions } from "@/components/data-table/hooks/useCreateDataTableOptions";
import Field from "@/components/Field";
import Form from "@/components/Form";
import SaveButton from "@/components/SaveButton";
import DateTimePicker from "@/components/DateTimePicker";
import TimeInput from "@/components/TimeInput";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/components/ui/use-toast";
import { useDynamicQueryParams } from "@/hooks/useDynamicQueryParams";
import useQueryData from "@/hooks/useQueryData";
import { ApiError } from "@/services/api";
import { FilterType } from "@/lib/filters";
import {
  ReadTimesheetEntry,
  WriteTimesheetEntry,
} from "@/types/timesheetEntry";
import { ReadTimesheetEntryContractor } from "@/types/timesheetEntryContractor";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery } from "@tanstack/react-query";
import { ClockPlus } from "lucide-react";
import { memo, useCallback, useMemo } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { generatePath, useParams } from "react-router-dom";

import { useSyncFormWithQueryData } from "../../../hooks/useSyncFormWithQueryData";
import { writeTimesheetEntryFormSchema } from "../schema";
import { TimesheetEntriesService } from "../services/timesheetEntries";
import { TimesheetEntriesContractorsService } from "../services/timesheetEntriesContractors";
import { WriteTimesheetEntryForm } from "../types";

import Spinner from "@/components/Spinner";
import { convertFormTimesheetEntryContractorsToWriteTimesheetEntry } from "../utils/convertFormTimesheetEntryContractorsToWriteTimesheetEntry";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import Title from "@/components/Title";
import DescriptionList from "@/components/description-list/DescriptionList";
import { useShiftReportQuery } from "../hooks/useShiftReportQuery";
import { PATH_CONTRACTOR, PATH_SHIFT_REPORT } from "@/constants";
import { DateTime } from "luxon";
import i18n from "@/i18n";
import { useMutationWithErrorHandling } from "@/hooks/useMutationWithErrorHandling";
import { useDatePickerProps } from "../hooks/useDatePickerProps";
import { EntityListLink } from "@/components/EntityListLink";

const defaultValues = {
  entries: {},
};

export const TimesheetEntriesContractorsForm = memo(() => {
  const name = "timesheetEntriesContractors";
  const { t } = useTranslation();
  const { shiftReportId } = useParams();
  const { toast } = useToast();

  const shiftReportQuery = useShiftReportQuery(shiftReportId as string);

  const queryFn = useCallback(() => {
    return TimesheetEntriesContractorsService.getList({
      filters: [
        {
          id: "shift_report_id",
          value: {
            value: shiftReportId as string,
            filter: FilterType.EqualsString,
          },
        },
      ],
    });
  }, [shiftReportId]);

  const options = useCreateDataTableOptions<ReadTimesheetEntryContractor>(name);

  const queryKey = useDynamicQueryParams(name, options);

  const listQuery = useQuery({
    queryKey,
    queryFn,
    refetchOnWindowFocus: false,
  });

  const data = useQueryData(listQuery);

  const allEntriesLocked = useMemo(() => {
    return data?.every((item) => !item.edit_allowed);
  }, [data]);

  const form = useForm<WriteTimesheetEntryForm>({
    resolver: zodResolver(writeTimesheetEntryFormSchema),
    defaultValues,
  });

  const mutation = useMutationWithErrorHandling<
    ReadTimesheetEntry,
    ApiError,
    WriteTimesheetEntry[],
    unknown
  >({
    errorTitle: t("timesheetEntries.visits.errors.update"),
    mutationFn: TimesheetEntriesService.patch,
    onSuccess: () => {
      form.reset(form.getValues());
      listQuery.refetch();
      toast({
        title: t("timesheetEntries.visits.success.update"),
      });
    },
  });

  const handleSubmit: SubmitHandler<WriteTimesheetEntryForm> = async (
    submittedData,
  ) => {
    const dirtyFields = form.formState.dirtyFields;
    const dirtyValues = Object.keys(dirtyFields.entries || {}).map((key) => {
      return submittedData.entries[key];
    });
    const payload = dirtyValues.map(
      convertFormTimesheetEntryContractorsToWriteTimesheetEntry,
    );
    return mutation.mutateAsync(payload);
  };

  const handleFillWithDefaultValues = useCallback(
    (item: ReadTimesheetEntryContractor) => {
      const shiftReport = shiftReportQuery.data;
      if (shiftReport === undefined) {
        return;
      }
      // shiftDate is YMD, no time set. We need to create shiftDate
      // in UTC as to not assume any local offset
      const shiftDate = DateTime.fromISO(shiftReport.shift_date, {
        zone: "utc",
      });

      // Default times are in HH:mm:ss, therefore we need to use proper locale
      // so the offset is correct
      const defaultStartTime = DateTime.fromFormat(
        item.default_start_time,
        "HH:mm:ss",
        {
          locale: i18n.language,
        },
      );

      const defaultEndTime = DateTime.fromFormat(
        item.default_end_time,
        "HH:mm:ss",
        {
          locale: i18n.language,
        },
      );

      const startDateTime = DateTime.fromObject({
        year: shiftDate.year,
        month: shiftDate.month,
        day: shiftDate.day,
        minute: defaultStartTime.minute,
        hour: defaultStartTime.hour,
      }).toISO();

      const isEndDateTimeTheNextDay = defaultEndTime < defaultStartTime;
      const shiftDateAdjustedForMidnight = shiftDate.plus({
        days: isEndDateTimeTheNextDay ? 1 : 0,
      });

      const endDateTime = DateTime.fromObject({
        year: shiftDateAdjustedForMidnight.year,
        month: shiftDateAdjustedForMidnight.month,
        day: shiftDateAdjustedForMidnight.day,
        minute: defaultEndTime.minute,
        hour: defaultEndTime.hour,
      }).toISO();

      form.setValue(`entries.${item.id}.start_datetime`, startDateTime ?? "", {
        shouldDirty: true,
      });
      form.setValue(`entries.${item.id}.end_datetime`, endDateTime ?? "", {
        shouldDirty: true,
      });
    },
    [form, shiftReportQuery.data],
  );

  const datePickerProps = useDatePickerProps(shiftReportQuery);

  useSyncFormWithQueryData(data, form);

  const { isSubmitting, disabled, isDirty } = form.formState;
  const isSubmitDisabled =
    allEntriesLocked || isSubmitting || disabled || !isDirty;

  if (listQuery.isLoading) {
    return <Spinner />;
  }

  return (
    <Form form={form} handleSubmit={handleSubmit}>
      <Title>{t("timesheetEntries.contractors.title")}</Title>
      <DescriptionList
        data={shiftReportQuery.data || {}}
        translationKey="timesheetEntries.contractors"
        variant="ghost"
        className="py-4"
      >
        <DescriptionList.Content className="p-0">
          <DescriptionList.Link
            name="shift"
            to={generatePath(PATH_SHIFT_REPORT, {
              shiftReportId: shiftReportQuery.data?.id || "",
            })}
          />
        </DescriptionList.Content>
      </DescriptionList>
      {data?.map((item) => (
        <Card key={item.id} className="@container">
          <CardHeader className="px-5 pt-3 pb-2 text-xs">
            <CardTitle className="flex items-center text-lg gap-3">
              <span>
                {item.contractor_name} ({item.contractor_user_ref_number})
                <EntityListLink
                  path={generatePath(PATH_CONTRACTOR, {
                    contractorId: item.contractor_id,
                  })}
                  className="w-6 h-6 ml-2"
                  target="_blank"
                />
              </span>
              <span className="text-muted-foreground">
                {item.payroll_department_name}
              </span>
            </CardTitle>
          </CardHeader>
          {!item.edit_allowed && (
            <CardContent className="p-3 pt-0">
              <Alert variant="warning">
                <AlertTitle>
                  {t("timesheetEntries.contractors.monthClosedTitle")}
                </AlertTitle>
                <AlertDescription>
                  {t("timesheetEntries.contractors.monthClosedDescription")}
                </AlertDescription>
              </Alert>
            </CardContent>
          )}
          <CardContent className="grid grid-cols-[repeat(2,max-content)] @[675px]:grid-cols-[repeat(4,max-content)] gap-2 px-5 pb-3 pt-0">
            <Field
              key="start_datetime"
              name={`entries.${item.id}.start_datetime`}
              required
              label={t("timesheetEntries.fields.start_datetime")}
              labelClassName="h-3"
              wrapperClassName="w-[165px]"
              render={({ field }) => (
                <DateTimePicker
                  {...field}
                  disabled={!item.edit_allowed}
                  datePickerProps={datePickerProps}
                />
              )}
            />
            <Field
              key="end_datetime"
              name={`entries.${item.id}.end_datetime`}
              required
              label={t("timesheetEntries.fields.end_datetime")}
              labelClassName="h-3"
              wrapperClassName="w-[165px]"
              render={({ field }) => (
                <DateTimePicker
                  {...field}
                  disabled={!item.edit_allowed}
                  datePickerProps={datePickerProps}
                />
              )}
            />
            <Field
              key="break_time"
              name={`entries.${item.id}.break_time`}
              label={t("timesheetEntries.fields.break_time")}
              labelClassName="h-3"
              wrapperClassName="w-[165px] @[675px]:w-[105px]"
              render={({ field }) => (
                <TimeInput
                  {...field}
                  disabled={!item.edit_allowed}
                  className="h-8 text-xs"
                />
              )}
            />
            <div className="flex justify-end pt-6">
              <Button
                variant="secondary"
                onClick={() => handleFillWithDefaultValues(item)}
                disabled={!item.edit_allowed}
                className="h-8 text-xs"
              >
                <ClockPlus className="h-4 w-4" />
                {t("timesheetEntries.contractors.fillWithDefaultValues")}
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}
      <footer className="flex justify-end">
        <SaveButton disabled={isSubmitDisabled} />
      </footer>
    </Form>
  );
});
